import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { Helmet } from "react-helmet-async";

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

// Mapping des routes vers les labels
const routeLabels: Record<string, string> = {
  "/": "Accueil",
  "/nos-offres": "Nos Offres",
  "/tutoriels": "Tutoriels",
  "/avis-clients": "Avis Clients",
  "/iptv-france": "IPTV France",
  "/abonnement-iptv": "Abonnement IPTV",
  "/iptv-pas-cher": "IPTV Pas Cher",
  "/tutoriels/iptv-iphone-ipad": "IPTV iPhone/iPad",
  "/tutoriels/iptv-android": "IPTV Android",
  "/tutoriels/iptv-firestick": "IPTV Firestick",
  "/tutoriels/iptv-smart-tv-android": "IPTV Smart TV",
  "/tutoriels/iptv-windows": "IPTV Windows",
  "/tutoriels/iptv-mac": "IPTV Mac",
  "/a-propos": "À propos",
  "/politique-de-confidentialite": "Politique de confidentialité",
  "/conditions-d-utilisation": "Conditions d'utilisation",
  "/mentions-legales": "Mentions légales",
  "/faq": "FAQ",
};

const Breadcrumbs = ({ items, className = "" }: BreadcrumbsProps) => {
  const location = useLocation();
  
  // Generate breadcrumbs from current path if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (items) return items;
    
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: "Accueil", href: "/" }];
    
    let currentPath = "";
    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`;
      const label = routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
      breadcrumbs.push({ label, href: currentPath });
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  
  // Don't show breadcrumbs on homepage
  if (location.pathname === "/") return null;

  // JSON-LD Schema for breadcrumbs
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `https://roidustreaming.fr${item.href}`
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav 
        aria-label="Fil d'Ariane" 
        className={`py-3 px-4 bg-background/50 border-b border-border/30 ${className}`}
      >
        <div className="container mx-auto">
          <ol className="flex items-center flex-wrap gap-1 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;
              
              return (
                <li 
                  key={item.href} 
                  className="flex items-center"
                  itemProp="itemListElement" 
                  itemScope 
                  itemType="https://schema.org/ListItem"
                >
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 mx-1 text-muted-foreground" aria-hidden="true" />
                  )}
                  
                  {isLast ? (
                    <span 
                      className="text-foreground font-medium"
                      itemProp="name"
                      aria-current="page"
                    >
                      {index === 0 && <Home className="w-4 h-4 inline mr-1" aria-hidden="true" />}
                      {item.label}
                    </span>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-muted-foreground hover:text-primary transition-colors"
                      itemProp="item"
                    >
                      <span itemProp="name">
                        {index === 0 && <Home className="w-4 h-4 inline mr-1" aria-hidden="true" />}
                        {item.label}
                      </span>
                    </Link>
                  )}
                  <meta itemProp="position" content={String(index + 1)} />
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;
